import { useEffect } from 'react';
import { useLogger } from '@archipro-website/logger/client';
import { RoccoLink } from '@rocco/components/link/RoccoLink';
import { cn } from '@rocco/utils/cn';
import { useRootData } from '@modules/root';
import { useConsentNotice } from './use-consent-notice';

/**
 * Houzz-style passive cookie notice for markets in `consentMode = 'notice'`.
 * The banner is purely SSR-friendly markup wrapped around the
 * `useConsentNotice` hook; everything else (hydration, acknowledgement cookie,
 * config validation) lives in the hook.
 *
 * When `consentMode === 'notice'` but the deployment is on a real public host
 * with no `cookies.topDomain` configured, the acknowledgement cookie cannot
 * survive subdomain navigation. We log a warning in that case so prod
 * misconfigurations surface in the logs without blocking local dev (where
 * `topDomain` is intentionally absent and host-scoped is fine).
 *
 * The card is mounted in the root layout and stays visible across Remix client
 * navigations until the visitor dismisses it (Houzz-style notice, not a
 * per-page modal).
 */
export const UsNoticeBanner = () => {
    const { config, env, localisation } = useRootData();
    const logger = useLogger();
    const { consentMode } = localisation.countryConfig;
    const topDomain = config.cookies.topDomain;
    const { shouldShow, dismiss } = useConsentNotice({
        consentMode,
        topDomain,
    });

    useEffect(() => {
        const SHARED_ENVS = ['Production', 'Staging', 'Development'];
        if (
            consentMode === 'notice' &&
            !topDomain &&
            SHARED_ENVS.includes(env.stage)
        ) {
            logger.warning(
                'UsNoticeBanner: notice consent mode is missing cookies.topDomain; ' +
                    'falling back to a host-scoped acknowledgement cookie.',
            );
        }
    }, [consentMode, topDomain, env.stage, logger]);

    if (!shouldShow) {
        return null;
    }

    return (
        <div
            className={cn(
                'fixed bottom-4 right-4 z-1000 max-w-[360px] rounded-lg border border-gray-200 bg-white p-4 shadow-lg',
                'text-sm leading-relaxed text-gray-800',
            )}
            role="status"
            aria-live="polite"
        >
            <button
                type="button"
                onClick={dismiss}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                aria-label="Dismiss cookie notice"
            >
                ×
            </button>
            <p className="pr-6">
                We use cookies and similar technologies to operate and improve
                ArchiPro. For more information see our{' '}
                <RoccoLink
                    to="/privacy-policy"
                    isNonRemixRoute
                    className="underline"
                >
                    Privacy Policy
                </RoccoLink>{' '}
                and{' '}
                <RoccoLink
                    to="/cookie-policy"
                    isNonRemixRoute
                    className="underline"
                >
                    Cookie Policy
                </RoccoLink>
                .
            </p>
        </div>
    );
};
